/**
 * /profile — user's own profile page.
 *
 * Currently houses the "My tickets & issues" section (MIRA feedback
 * history). Mounted in App.js under the protected Layout.
 */
import { User as UserIcon, Mail, Shield, MessageCircle, Languages } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n";
import { UserPreferences } from "@/components/common/UserPreferences";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import MyTicketsPanel from "@/components/feedback/MyTicketsPanel";

export default function Profile() {
  const { user } = useAuth();
  const { t } = useI18n();
  // MIRA ticket #267 — surface a "Show Mira" toggle when the user
  // previously hid the widget. Reads localStorage once on mount and
  // listens for cross-tab storage events so the badge stays in sync.
  const [miraHidden, setMiraHidden] = useState(() => {
    try { return localStorage.getItem("mira_hidden") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "mira_hidden") setMiraHidden(e.newValue === "1");
    };
    // Same-tab signal — when the widget fires `mira:show` / `mira:hide`
    // from its own buttons (or from another React tree), reflect it
    // here too so the switch flips without a reload (#271).
    const onShow = () => setMiraHidden(false);
    const onHide = () => setMiraHidden(true);
    window.addEventListener("storage", onStorage);
    window.addEventListener("mira:show", onShow);
    window.addEventListener("mira:hide", onHide);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("mira:show", onShow);
      window.removeEventListener("mira:hide", onHide);
    };
  }, []);
  const showMira = () => {
    try { localStorage.setItem("mira_hidden", "0"); } catch { /* private mode */ }
    setMiraHidden(false);
    // Same-tab signal — the widget listens for this and re-renders.
    window.dispatchEvent(new Event("mira:show"));
    toast.success("Mira is back");
  };
  // #271 — the profile toggle can now also HIDE Mira (previously the
  // only hide path was the eye-off button inside the widget itself).
  const hideMira = () => {
    try { localStorage.setItem("mira_hidden", "1"); } catch { /* private mode */ }
    setMiraHidden(true);
    window.dispatchEvent(new Event("mira:hide"));
    toast("Mira hidden", {
      description: "Flip this switch anytime to bring the assistant back.",
      classNames: { description: "!text-slate-700 !opacity-100" },
    });
  };
  const toggleMira = (checked) => (checked ? showMira() : hideMira());

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="profile-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-violet-300 text-violet-950 text-lg font-semibold grid place-items-center">
            {((user?.name || user?.email || "?")
              .split(/[\s.@]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((s) => s[0]?.toUpperCase() || "")
              .join("")) || <UserIcon className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="profile-name">
              {user?.name || user?.email || "—"}
            </h1>
            <div className="flex items-center gap-2 text-[12.5px] text-slate-600 mt-0.5">
              <Mail className="w-3.5 h-3.5" />
              <span data-testid="profile-email">{user?.email || "—"}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-[10px] font-mono uppercase" data-testid="profile-role">
                <Shield className="w-3 h-3 mr-1" />
                {user?.role || "viewer"}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="profile-preferences-card">
        <div className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-1.5 mb-1">
          <Languages className="w-3.5 h-3.5 text-slate-400" />
          {t("My defaults")}
        </div>
        <UserPreferences showLanguageHint variant="rows" />
      </div>


      {/* MIRA support assistant — visibility toggle (#267, upgraded to
          an always-visible on/off switch in #271 so users can hide OR
          unhide without hunting for the widget's eye-off button). */}
      <div
        className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5 flex items-center justify-between gap-3 shadow-sm"
        data-testid="profile-mira-toggle-row"
      >
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-9 w-9 rounded-full bg-rose-600 overflow-hidden ring-1 ring-rose-300/60 grid place-items-center shrink-0">
            <img
              src="/marfi-logo.jpeg"
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5 text-slate-400" />
              {t("Mira assistant")}
            </div>
            <div className="text-[12px] text-slate-500">
              {miraHidden
                ? t("Hidden — the floating support pill won't appear on any page.")
                : t("Visible — the floating support pill appears bottom-right on every page.")}
            </div>
          </div>
        </div>
        <Switch
          checked={!miraHidden}
          onCheckedChange={toggleMira}
          aria-label="Toggle Mira assistant visibility"
          data-testid="profile-mira-switch"
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <MyTicketsPanel inline />
      </div>
    </div>
  );
}
